import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Text, TextInput } from '@mantine/core';
import { IconShieldLock } from '@tabler/icons-react';
import {
  useRidesMeService,
  useRidesBookingsService,
  useRidesMatchesService,
  RIDES_ERROR_CODES,
  RIDES_VERIFICATION_TYPE,
  type RidesDocumentCheckResult,
} from '@actschurch/ccms-sdk';
import { riderMissingStep } from '@/store/ridesWizard';
import { documentRules, driverStandards, photoRules, useCopy, visiblePhoto, type RequirementLevel } from '@/lib/settings';
import { hasErrorCode } from '@/lib/copy';
import { docsUsable, splitMissing, useMyDocuments } from '@/lib/documents';
import { EMPTY_EMERGENCY, emergencyChanged, emergencyFromView, emergencyRequest, emergencyValid, type EmergencyDraft } from '@/lib/driverStandards';
import { useInvalidateMe, findVerification, isSubmitted, isVerified } from '@/lib/me';
import { ridesKeys } from '@/lib/queryKeys';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { SectionCard } from '@/components/ui/SectionCard';
import { Callout } from '@/components/ui/Callout';
import { StatusPill } from '@/components/ui/StatusPill';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { IdentityDocumentForm } from '@/components/rides/IdentityDocumentForm';
import { GuardianConsent, GuardianDetailsFields, guardianDetailsComplete } from '@/components/rides/GuardianBox';
import { ConsentCheckbox } from '@/components/rides/ConsentCheckbox';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { DocumentsCard } from '@/components/rides/DocumentsCard';
import { EmergencyContactCard } from '@/components/rides/EmergencyContactCard';
import { LiftCard } from '@/components/flows/LiftCard';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { riderMatchQuery } from './Matches';
import { riderPlan, sexFromGender, useRiderFlow, whoLabel, type RiderStepKey } from './_shared';

type Part = 'id' | 'child' | 'guardian' | 'guardian-id' | 'book';
const PARTS: Part[] = ['id', 'child', 'guardian', 'guardian-id', 'book'];

/**
 * Find a ride, the last screens: check the ID once, then book.
 *   id           your ID check (first booking only), with the privacy consent
 *   child        the child's name (booking for a child; 400 child_name_required)
 *   guardian     the parent or guardian's details
 *   guardian-id  the parent or guardian's ID check (once)
 *   book         the lift being booked (driver, car, plate, time, campuses), anything the settings
 *                still ask for (documents, emergency contact, consents) and "Book this seat"
 * The booking 409s (documents / emergency contact / child-safety clearance) point at the section to fix.
 */
export default function RideVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const { t, settings } = useCopy();
  const f = useRiderFlow();
  const { rider, setRider, resetRider, verifications, profile, member, memberIsMinor, forMinor, event } = f;
  const me = useRidesMeService();
  const bookingsSvc = useRidesBookingsService();
  const matchesSvc = useRidesMatchesService();
  const invalidate = useInvalidateMe();
  const docs = useMyDocuments();
  const { getUserDisplayName } = useEnrichedAuth();

  const [dataConsent, setDataConsent] = useState(false);
  const [guardianConsent, setGuardianConsent] = useState(false);
  const [childName, setChildName] = useState('');
  const [tried, setTried] = useState(false);
  const [emergency, setEmergency] = useState<EmergencyDraft>(EMPTY_EMERGENCY);
  const consentRef = useRef<HTMLDivElement>(null);
  const guardianConsentRef = useRef<HTMLDivElement>(null);
  // Freeze which ID checks were still to do when these screens opened, so the step count doesn't
  // jump when a check succeeds on the way.
  const [frozen, setFrozen] = useState<{ idDone: boolean; guardianIdDone: boolean } | null>(null);
  useEffect(() => {
    if (!frozen && verifications.isSuccess && !verifications.isFetching) setFrozen({ idDone: f.idDone, guardianIdDone: f.guardianIdDone });
  }, [frozen, verifications.isSuccess, verifications.isFetching, f.idDone, f.guardianIdDone]);
  const flags = frozen ?? { idDone: f.idDone, guardianIdDone: f.guardianIdDone };
  const plan = useMemo(() => riderPlan({ needsService: f.needsService, forMinor, memberIsMinor, ...flags }), [f.needsService, forMinor, memberIsMinor, flags.idDone, flags.guardianIdDone]); // eslint-disable-line react-hooks/exhaustive-deps
  const stepOf = (k: RiderStepKey) => Math.max(1, plan.indexOf(k) + 1);

  const savedEmergency = profile.data?.emergencyContact;
  useEffect(() => {
    setEmergency(emergencyFromView(savedEmergency));
  }, [savedEmergency?.name, savedEmergency?.phone, savedEmergency?.relationship]); // eslint-disable-line react-hooks/exhaustive-deps

  // A parent booking for a child is usually the guardian: fill in their own name and number once.
  useEffect(() => {
    if (!forMinor || memberIsMinor || !member.data) return;
    const g = rider.guardian;
    if (g.name || g.mobile) return;
    const name = getUserDisplayName()?.trim() || [member.data.firstName, member.data.lastName].filter(Boolean).join(' ').trim();
    if (name || member.data.cellNumber) setRider({ guardian: { ...g, name, mobile: member.data.cellNumber ?? '' } });
  }, [forMinor, memberIsMinor, member.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // The lift being booked (from the matches the rider just saw; refetched if the cache is gone).
  const missing = riderMissingStep(rider, 'verify');
  const query = riderMatchQuery(rider);
  const matchQ = useQuery({ queryKey: ridesKeys.matches(query), queryFn: () => matchesSvc.find(query), staleTime: 15_000, enabled: !missing });
  const chosen = matchQ.data?.drivers.find((d) => d.rideAvailabilityID === rider.chosenAvailabilityId);

  const lookupsSettings = f.lookups.data?.settings;
  const idV = findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId);
  const guardianV = findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GuardianId);
  const idOk = !f.idRequired || isSubmitted(idV);
  const guardianOk = !f.idRequired || isSubmitted(guardianV);
  // The API only books with a VERIFIED check (when the settings ask for one); "sent" is not enough.
  const checkPending = f.idRequired && (forMinor ? guardianOk && !isVerified(guardianV) : idOk && !isVerified(idV));
  const declaredSexId = rider.party === 'solo' && !forMinor ? rider.soloSexId : profile.data?.sexID || sexFromGender(member.data?.gender);
  const consentDone = !!profile.data?.dataConsentGranted || dataConsent;
  const childOk = memberIsMinor || childName.trim().length >= 2;

  const standards = driverStandards(settings);
  const docRules = documentRules(settings);
  const riderDocs = docRules.enabled && docsUsable(docs.data) ? splitMissing(docs.data, docs.data.missingForRider ?? []) : null;
  const docsOutstanding = !!riderDocs && riderDocs.toUpload.length + riderDocs.awaitingReview.length > 0;

  /** Save the rider profile (sex + privacy consent) before the first document check, only once the member has agreed. */
  const ensureProfile = async (p: { sexId: number | null }) => {
    if (!consentDone) throw new Error('Please tick the box above to agree before we check the ID.');
    const sexID = p.sexId ?? declaredSexId ?? 0;
    if (profile.data?.dataConsentGranted && profile.data.sexID === sexID) return;
    await me.upsertProfile({ sexID, dataConsent: { granted: true, textVersion: lookupsSettings?.dataConsentTextVersion ?? '1' } });
    qc.invalidateQueries({ queryKey: ridesKeys.me.profile });
  };
  /** Before the guardian's document check: record the privacy consent (the profile keeps the member's own sex). */
  const ensureConsent = async () => {
    if (!consentDone) throw new Error('Please tick the box above to agree before we check the ID.');
    if (profile.data?.dataConsentGranted) return;
    await me.upsertProfile({
      sexID: profile.data?.sexID || sexFromGender(member.data?.gender) || 0,
      dataConsent: { granted: true, textVersion: lookupsSettings?.dataConsentTextVersion ?? '1' },
    });
    qc.invalidateQueries({ queryKey: ridesKeys.me.profile });
  };
  const onDocResult = (_r: RidesDocumentCheckResult) => invalidate();

  const book = useMutation({
    mutationFn: async () => {
      const needsProfile = !profile.data?.dataConsentGranted;
      const sendEmergency = emergencyLevel !== 'notAsked' && emergencyChanged(emergency, savedEmergency) && emergencyValid(emergency, emergencyLevel);
      if (needsProfile || sendEmergency) {
        await me.upsertProfile({
          sexID: profile.data?.sexID || declaredSexId || 0,
          dataConsent: needsProfile ? { granted: true, textVersion: lookupsSettings?.dataConsentTextVersion ?? '1' } : null,
          emergencyContact: sendEmergency ? emergencyRequest(emergency) : undefined,
        });
        qc.invalidateQueries({ queryKey: ridesKeys.me.profile });
      }
      return bookingsSvc.create({
        rideAvailabilityID: rider.chosenAvailabilityId!,
        partyMaleCount: rider.maleCount,
        partyFemaleCount: rider.femaleCount,
        forMinor,
        childName: forMinor && !memberIsMinor ? childName.trim() : null,
        guardianName: forMinor ? rider.guardian.name.trim() : null,
        guardianRelationship: forMinor ? rider.guardian.relationship : null,
        guardianMobile: forMinor ? rider.guardian.mobile.trim() : null,
        guardianConsent: forMinor ? { granted: true, textVersion: lookupsSettings?.guardianConsentTextVersion ?? '1' } : null,
      });
    },
    onError: (e) => {
      // Something required may have changed since the page loaded: refresh what we show.
      if (hasErrorCode(e, RIDES_ERROR_CODES.DocumentsRequired)) qc.invalidateQueries({ queryKey: ridesKeys.me.documents });
      if (hasErrorCode(e, RIDES_ERROR_CODES.ChildNameRequired)) navigate('/ride/verify?part=child');
    },
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ridesKeys.bookings.all });
      qc.invalidateQueries({ queryKey: ridesKeys.me.summary });
      resetRider();
      navigate(`/ride/confirm/${b.rideBookingID}`, { replace: true });
    },
  });

  const docsRefused = hasErrorCode(book.error, RIDES_ERROR_CODES.DocumentsRequired);
  const emergencyRefused = hasErrorCode(book.error, RIDES_ERROR_CODES.EmergencyContactRequired);
  const clearanceRefused = hasErrorCode(book.error, RIDES_ERROR_CODES.DriverNotClearedForMinors);
  const childRefused = hasErrorCode(book.error, RIDES_ERROR_CODES.ChildNameRequired);
  // A 409 emergency_contact_required means it IS required now, whatever the cached settings said.
  const emergencyLevel: RequirementLevel = emergencyRefused ? 'required' : standards.riderEmergency;
  const emergencyOk = emergencyValid(emergency, emergencyLevel);

  if (missing) return <Navigate to={missing} replace />;
  // Older saved progress may not say who is riding yet.
  if (!memberIsMinor && rider.forMinor === null && !profile.isLoading) return <Navigate to="/ride/party" replace />;

  const loading = verifications.isLoading || profile.isLoading || member.isLoading || f.lookups.isLoading;
  const loadError = verifications.error ?? profile.error ?? null;

  // Which screen: ?part=, else the first one this member still needs.
  const plannedParts = plan.filter((k): k is Part => (PARTS as RiderStepKey[]).includes(k));
  const asked = params.get('part') as Part | null;
  const part: Part = asked && PARTS.includes(asked) ? asked : plannedParts[0] ?? 'book';
  const needsChildFirst = forMinor && !memberIsMinor && !childOk && (part === 'guardian' || part === 'guardian-id' || part === 'book');
  if (!loading && needsChildFirst) return <Navigate to="/ride/verify?part=child" replace />;
  if (!loading && !forMinor && (part === 'child' || part === 'guardian' || part === 'guardian-id')) return <Navigate to="/ride/verify" replace />;
  if (!loading && forMinor && part === 'id') return <Navigate to="/ride/verify" replace />;

  const nextAfter = (k: Part): string => {
    const i = plan.indexOf(k);
    const nxt = plan[i + 1] as Part | undefined;
    return nxt && nxt !== 'book' ? `/ride/verify?part=${nxt}` : '/ride/verify?part=book';
  };
  const prevOf = (k: Part): string => {
    const i = plan.indexOf(k);
    const prv = i > 0 ? plan[i - 1] : 'driver';
    return prv === 'driver' ? '/ride/matches' : `/ride/verify?part=${prv}`;
  };
  const go = (to: string) => { setTried(false); navigate(to); };
  const stepKey: RiderStepKey = part === 'book' ? 'book' : part;
  const chips = [...f.chips('driver'), ...(chosen ? [`With ${chosen.driverName.split(' ')[0]}`] : [])];
  const first = childName.trim().split(/\s+/)[0] ?? '';

  const titles: Record<Part, string> = {
    id: 'Check your ID',
    child: "What is your child's name?",
    guardian: memberIsMinor ? 'Your parent or guardian' : 'Your details as parent or guardian',
    'guardian-id': memberIsMinor ? "Your parent or guardian's ID" : 'Check your ID',
    book: 'Check and book',
  };
  const ledes: Record<Part, string> = {
    id: t('ride.id_intro'),
    child: t('ride.child_intro'),
    guardian: t('ride.guardian_intro'),
    'guardian-id': t('ride.guardian_id_intro'),
    book: t('ride.book_intro'),
  };

  // ---------------------------------------------------------------- the booking screen's checks
  const bookProblems: { key: string; fix: () => void }[] = [];
  if (!forMinor && !idOk) bookProblems.push({ key: 'id', fix: () => go('/ride/verify?part=id') });
  if (forMinor && !childOk) bookProblems.push({ key: 'child', fix: () => go('/ride/verify?part=child') });
  if (forMinor && !guardianDetailsComplete(rider.guardian)) bookProblems.push({ key: 'guardian', fix: () => go('/ride/verify?part=guardian') });
  if (forMinor && !guardianOk) bookProblems.push({ key: 'guardian-id', fix: () => go('/ride/verify?part=guardian-id') });
  if (forMinor && !guardianConsent) bookProblems.push({ key: 'guardian-consent', fix: () => guardianConsentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }) });
  if (!consentDone) bookProblems.push({ key: 'consent', fix: () => consentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }) });
  if (!emergencyOk) bookProblems.push({ key: 'emergency', fix: () => document.getElementById('emergency')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) });
  if (docsOutstanding) bookProblems.push({ key: 'docs', fix: () => document.getElementById('rider-docs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) });
  const onBook = () => {
    if (checkPending && !bookProblems.length) { navigate('/home'); return; }
    setTried(true);
    if (bookProblems.length) { bookProblems[0].fix(); return; }
    book.mutate();
  };
  const chooseAnotherDriver = () => {
    qc.invalidateQueries({ queryKey: ['rides', 'matches'] });
    setRider({ chosenAvailabilityId: null });
    navigate('/ride/matches');
  };

  // ---------------------------------------------------------------- render
  let body: ReactNode = null;
  let action: ReactNode = null;

  if (part === 'id') {
    body = (
      <div className="rides-flow-stack">
        {!profile.data?.dataConsentGranted && (
          <div ref={consentRef}><ConsentCheckbox checked={dataConsent} onChange={setDataConsent} /></div>
        )}
        {idOk ? (
          <Callout tone={isVerified(idV) ? 'ok' : 'info'} title={isVerified(idV) ? 'Your ID is checked' : 'Your details look right'}>
            {isVerified(idV) ? "You don't need to do this again." : t('ride.id_pending_block')}
          </Callout>
        ) : (
          <SectionCard title="Your ID">
            <IdentityDocumentForm
              typeId={RIDES_VERIFICATION_TYPE.GovernmentId}
              expectedSexId={declaredSexId}
              expectedDob={member.data?.dateOfBirth ?? null}
              beforeSubmit={ensureProfile}
              onResult={onDocResult}
              submitLabel="Check my ID"
            />
          </SectionCard>
        )}
      </div>
    );
    if (idOk) action = <Button fullWidth onClick={() => go(nextAfter('id'))}>Continue</Button>;
  }

  if (part === 'child') {
    const err = (tried && !childOk) || childRefused ? t('booking.child_name_required') : undefined;
    body = (
      <div className="rides-flow-stack">
        <TextInput
          label="Child's name"
          description="First name and surname"
          autoComplete="off"
          value={childName}
          onChange={(e) => { setChildName(e.currentTarget.value); if (book.error) book.reset(); }}
          maxLength={100}
          required
          error={err}
          data-autofocus
        />
      </div>
    );
    action = <Button fullWidth onClick={() => { setTried(true); if (childOk) go(nextAfter('child')); }}>Continue</Button>;
  }

  if (part === 'guardian') {
    body = <GuardianDetailsFields guardian={rider.guardian} onChange={(g) => setRider({ guardian: g })} showErrors={tried} />;
    action = <Button fullWidth onClick={() => { setTried(true); if (guardianDetailsComplete(rider.guardian)) go(nextAfter('guardian')); }}>Continue</Button>;
  }

  if (part === 'guardian-id') {
    body = (
      <div className="rides-flow-stack">
        {!profile.data?.dataConsentGranted && (
          <div ref={consentRef}><ConsentCheckbox checked={dataConsent} onChange={setDataConsent} /></div>
        )}
        {guardianOk ? (
          <Callout tone={isVerified(guardianV) ? 'ok' : 'info'} title={isVerified(guardianV) ? 'The ID is checked' : 'The details look right'}>
            {isVerified(guardianV) ? "You don't need to do this again." : t('ride.guardian_pending_block')}
          </Callout>
        ) : (
          <SectionCard title={memberIsMinor ? "Your parent or guardian's ID" : 'Your ID'}>
            <IdentityDocumentForm
              typeId={RIDES_VERIFICATION_TYPE.GuardianId}
              whose={memberIsMinor ? "the guardian's" : 'your'}
              beforeSubmit={ensureConsent}
              onResult={onDocResult}
              submitLabel="Check the ID"
            />
          </SectionCard>
        )}
      </div>
    );
    if (guardianOk) action = <Button fullWidth onClick={() => go(nextAfter('guardian-id'))}>Continue</Button>;
  }

  if (part === 'book') {
    const photo = visiblePhoto(chosen?.driverPhotoUrl, photoRules(settings).driverToRiders);
    body = (
      <div className="rides-flow-stack">
        {matchQ.isLoading && <LoadingState label="Loading your lift" count={1} />}
        {chosen ? (
          <LiftCard
            eyebrow="Your lift"
            driverName={chosen.driverName}
            driverSexId={chosen.driverSexID}
            driverPhotoUrl={photo}
            vehicle={chosen.vehicle}
            date={chosen.date}
            departureTime={chosen.departureTime}
            serviceTimeStart={event?.usesCampusServiceTimes ? chosen.serviceTimeStart : null}
            collectionName={chosen.collectionName}
            dropOffName={chosen.dropOffName}
            eventName={chosen.eventName}
            notes={chosen.notes}
          />
        ) : matchQ.data ? (
          <Callout tone="warn" title="This lift is no longer available" action={<Button variant="default" onClick={chooseAnotherDriver}>Choose another driver</Button>}>
            The driver may have filled their seats or changed their plans.
          </Callout>
        ) : null}

        {checkPending && (
          <Callout tone="warn" title="You can't book yet">{t(forMinor ? 'ride.guardian_pending_block' : 'ride.id_pending_block')}</Callout>
        )}

        <SectionCard title="Who's travelling" flush>
          <ul className="rides-review">
            {forMinor && !memberIsMinor ? (
              <li>
                <span className="txt"><span className="k">Child</span><span className="v">{childName.trim()}{rider.party === 'group' && <small>{whoLabel(rider)}</small>}</span></span>
                <Link className="rides-linkbtn chg" to="/ride/verify?part=child">Change</Link>
              </li>
            ) : (
              <li>
                <span className="txt"><span className="k">Travelling</span><span className="v">{whoLabel(rider) || 'Just me'}</span></span>
                <Link className="rides-linkbtn chg" to="/ride/party">Change</Link>
              </li>
            )}
            {forMinor && (
              <li>
                <span className="txt">
                  <span className="k">Parent or guardian</span>
                  <span className="v">{rider.guardian.name.trim()}<small>{[rider.guardian.relationship, rider.guardian.mobile].filter(Boolean).join(', ')}</small></span>
                </span>
                <Link className="rides-linkbtn chg" to="/ride/verify?part=guardian">Change</Link>
              </li>
            )}
            {f.idRequired && (
              <li>
                <span className="txt"><span className="k">{forMinor ? "Parent or guardian's ID check" : 'Your ID check'}</span></span>
                {(forMinor ? guardianOk : idOk)
                  ? <StatusPill tone={isVerified(forMinor ? guardianV : idV) ? 'ok' : 'info'}>{isVerified(forMinor ? guardianV : idV) ? 'Done' : 'Being checked'}</StatusPill>
                  : <StatusPill tone="warn">To do</StatusPill>}
              </li>
            )}
          </ul>
        </SectionCard>

        {forMinor && (
          <div ref={guardianConsentRef} style={{ scrollMarginTop: 80 }}>
            <SectionCard title="Your consent" description={t('ride.guardian_consent_intro')}>
              <GuardianConsent checked={guardianConsent} onChange={setGuardianConsent} error={tried && !guardianConsent} />
            </SectionCard>
          </div>
        )}

        <div id="rider-docs" style={{ scrollMarginTop: 80 }}>
          <DocumentsCard context="rider" highlight={docsRefused || (tried && docsOutstanding)} onChanged={() => book.reset()} />
        </div>

        {emergencyLevel !== 'notAsked' && (
          <EmergencyContactCard id="emergency" value={emergency} onChange={setEmergency} level={emergencyLevel} highlight={emergencyRefused || (tried && !emergencyOk)} disabled={book.isPending} />
        )}

        {!profile.data?.dataConsentGranted && (
          <div ref={consentRef} style={{ scrollMarginTop: 80 }}>
            <ConsentCheckbox checked={dataConsent} onChange={setDataConsent} />
            {tried && !consentDone && <Text c="var(--rides-bad)" fw={600} mt={6} role="alert">Please tick the box to agree.</Text>}
          </div>
        )}

        <Callout tone="info">{t('ride.book_tip')}</Callout>

        {clearanceRefused && (
          <Callout tone="bad" icon={<IconShieldLock size={20} />} live action={<Button variant="default" onClick={chooseAnotherDriver}>Choose another driver</Button>}>
            {t('booking.clearance_required')}
          </Callout>
        )}
        {!(docsRefused || emergencyRefused || clearanceRefused || childRefused) && <ErrorAlert error={book.error} title="We couldn't book that seat" />}
      </div>
    );
    action = (
      <Button fullWidth loading={book.isPending} onClick={onBook} disabled={matchQ.isSuccess && !chosen && !checkPending}>
        {checkPending && !bookProblems.length ? 'Back to Home' : forMinor && first ? `Book a seat for ${first}` : 'Book this seat'}
      </Button>
    );
  }

  return (
    <div className="rides-main rides-flow">
      <StepHeader step={stepOf(stepKey)} total={plan.length} title={titles[part]} lede={ledes[part]} backTo={prevOf(part)} chips={chips} />
      {loading ? (
        <LoadingState label="Loading your details" count={2} />
      ) : loadError ? (
        <ErrorState title="We couldn't load your details" error={loadError} onRetry={() => { verifications.refetch(); profile.refetch(); }} />
      ) : (
        <>
          <div className="rides-stepbody" key={part ?? 'none'}>{body}</div>
          {action && (
            <StickyActionBar hint={part === 'book' && tried && bookProblems.length ? 'Please finish the items marked above.' : part === 'book' && checkPending ? 'Your choices are saved.' : undefined}>
              {action}
            </StickyActionBar>
          )}
        </>
      )}
    </div>
  );
}
